import React, { MouseEventHandler, useContext, useRef, useEffect } from 'react';
import { CarouselProvider, CarouselContext, DotGroup, Image, Slide, Slider } from 'pure-react-carousel';
import './ClpCarousel.css';
import itly from 'itly';

interface ClpCarouselProps {
  images: string[];
  productName: string;
  cardIndex: number;
}

export const ClpCarousel: React.FC<ClpCarouselProps> = props => {
  return (
    <CarouselProvider naturalSlideWidth={300} naturalSlideHeight={300} totalSlides={props.images.length}>
      <Carousel {...props} />
    </CarouselProvider>
  );
};

const Carousel: React.FC<ClpCarouselProps> = ({ images, productName, cardIndex }) => {
  const { getStoreState: getCarouselState, subscribe, unsubscribe } = useContext(CarouselContext);
  const lastSlide = useRef(0);
  const mouseDownPosition = useRef([0, 0]);

  const handleSlideMouseDown: MouseEventHandler<HTMLImageElement> = event => {
    mouseDownPosition.current = [event.clientX, event.clientY];
  };

  const handleSlideClick: MouseEventHandler<HTMLImageElement> = event => {
    // Safari triggers onClick even if the user is dragging the carousel, so we need to compare
    // the mousedown position to the click position to avoid nav to PDP on drag
    const [mouseDownX, mouseDownY] = mouseDownPosition.current;
    if (mouseDownX !== event.clientX || mouseDownY !== event.clientY) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
  };

  const handleDotGroupClick: MouseEventHandler<HTMLDivElement> = event => {
    // Prevent event from bubbling up and navigating to PDP so that users
    // can change slides with keyboard controls
    event.stopPropagation();
    event.preventDefault();
  };

  useEffect(() => {
    const onChange = () => {
      const { currentSlide } = getCarouselState();
      if (currentSlide !== lastSlide.current) {
        itly.clpLinkSwipe({
          product_name: productName,
          card_number: cardIndex + 1
        });
        lastSlide.current = currentSlide;
      }
    };

    subscribe(onChange);
    return () => unsubscribe(onChange);
  }, [getCarouselState, productName, subscribe, cardIndex, unsubscribe]);

  return (
    <>
      <Slider className="clp-slider">
        {images.map((image, index) => {
          const alt = `${productName} image ${index + 1}`;
          return (
            <Slide
              key={image + index}
              index={index}
              className="clp-slide"
              onClick={handleSlideClick}
              onMouseDown={handleSlideMouseDown}
            >
              <Image src={image} alt={alt} hasMasterSpinner={false} />
            </Slide>
          );
        })}
      </Slider>

      <DotGroup className="clp-dot-group" onClick={handleDotGroupClick} />
    </>
  );
};
